/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
// import Head from 'next/head';
import { NextPagesNavigator, NoSSR } from '@meitu/catalyze-next';
import { getEnv, navigate, statAgent } from '@/common/agents';
import { defineNextSubApp, renderNextPage } from '@/common/next';
import { createErrorMiddleware } from '@/apiSdk/errorMiddleware';

import { getIsOverseaByCountryCode } from '$/common/utils/countryCodeUtil';
import localStorageUtil from '$/common/utils/localStorageUtil';
import { LanguageCodeType } from '$/store/languageStore';
import { InitComponent } from '$/store/useOfficialStore';
import {
  getChannel, getMatrixChannel, initChannel, getFirstURL,
} from '$/utils/channelUtil';
import Script from 'next/script';

import { getGid } from '@/utils';
import { getAccessToken, getUID, initMTLogin } from '@/common/utils/accountUtil';
import { loadMtstatSdkScript } from '@/common/loadMtstatSdkScript';
import { createCanvasErrorMiddleware } from '@/apiSdk/errorCanvasMiddleware';
import { LanguageCode } from '$/store/languageStore/types';
import { isInLangList, mapLanguageCode } from '$/store/languageStore/utils';

/** Google Analytics ID，国内场景不传入也不加载脚本 */
const GOOGLE_TARGET_ID = 'G-GJ1BR2FCKG';

/** 渲染移动端外层，并按地区控制 Google Analytics 加载 */
function Content({ appProps }: any) {
  const { countryCode } = appProps.pageProps;
  const shouldLoadOverseaAnalytics = getIsOverseaByCountryCode(countryCode);

  return (
    <>
      {shouldLoadOverseaAnalytics ? (
        <>
          <Script
            id="mobile-google-analytics-script"
            src={`https://www.googletagmanager.com/gtag/js?id=${GOOGLE_TARGET_ID}`}
            strategy="afterInteractive"
          />
          <Script
            id="mobile-google-analytics-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag(){window.dataLayer.push(arguments);}
              gtag('js', new Date());
              gtag('config', ${JSON.stringify(GOOGLE_TARGET_ID)});
            `,
            }}
          />
        </>
      ) : null}

      {/* 该组件是必需的，否则navigate()不会生效。 */}
      <NextPagesNavigator navigate={navigate} />
      {renderNextPage(appProps)}
    </>
  );
}
export default defineNextSubApp<{
  InitialData: {
    languageCode: LanguageCodeType;
  };
}>({
  component: ({ appProps, initialData }) => {
    return (
      <NoSSR>
        {/* 根store */}
        {/* {renderNextPage(appProps)} */}
        <Content appProps={appProps} />
      </NoSSR>
    );
  },
  // 在水合前初始化渠道、语言、埋点和登录状态，避免业务组件读取到未初始化配置。
  async beforeHydration(data: any) {
    const userLang = localStorageUtil.getItem('lang');
    const searchParams = new URLSearchParams(window.location.search);
    const roomId = searchParams.get('room_id') || '';
    const shouldLoadOverseaAnalytics = getIsOverseaByCountryCode(data.props.pageProps.countryCode);
    let lang: LanguageCodeType = shouldLoadOverseaAnalytics ? LanguageCode.en : LanguageCode.zhHans;
    const urlLanguageCode = searchParams.get('language_code');
    const acceptLang = navigator.language.toLowerCase();
    // 链接参数
    if (isInLangList(urlLanguageCode as LanguageCodeType)) {
      lang = mapLanguageCode(urlLanguageCode as LanguageCodeType);
    } else if (isInLangList(userLang as LanguageCodeType)) {
      // local缓存参数
      lang = mapLanguageCode(userLang as LanguageCodeType);
    } else if (acceptLang) {
      // 浏览参数
      lang = mapLanguageCode(acceptLang as LanguageCodeType);
    }

    console.log('app_key', process.env.NEXT_PUBLIC_REBONEO_APP_KEY);
    initChannel();
    try {
      const channelValue = getChannel();
      await loadMtstatSdkScript();
      await statAgent.initialize({
        appKey: process.env.NEXT_PUBLIC_REBONEO_APP_KEY as string,
        uid: getUID(),
        env: getEnv().ENV === 'release' ? 'release' : 'test',
        channel: channelValue,
        moduleId: 'roboneo_web',
        commonParams: {
          room_id: roomId,
          first_url: getFirstURL(), // 渠道投放的落地页url
          channel: channelValue,
          matrix_channel: getMatrixChannel(), // 矩阵跳转时的标识
        },
        beforeLogin() { return true; },
        beforePageView(mtstat) {
          mtstat.setGid(getGid(), 'app');
          return true;
        },
      });
      const shareCoverUrl = getEnv().ENV === 'development'
        ? `${window.location.origin}/--roboneo/share_cover`
        : `${window.location.origin}/share_cover`;
      const shareTeamUrl = getEnv().ENV === 'development'
        ? `${window.location.origin}/--roboneo/team_share`
        : `${window.location.origin}/team_share`;
      const TOKEN = {
        development: 'A2600861ECF8CBF270D24AE3AD37F4BA',
        dev: 'A2600861ECF8CBF270D24AE3AD37F4BA',
        pre: 'A2600861ECF8CBF270D24AE3AD37F4BA',
        beta: '315F4D4D9343434994287A78EAC04076',
        release: '45C30555F10E49629098A75F95828DA6',
      };

      const token = getAccessToken();
      const uid = getUID();
      InitComponent({
        clientId: process.env.NEXT_PUBLIC_REBONEO_CLIENT_ID || '',
        env: getEnv().ENV,
        gid: getGid(),
        token: TOKEN[getEnv().ENV],
        firstUrl: getFirstURL(), // 渠道投放的落地页url
        channel: channelValue,
        shareCoverUrl,
        shareTeamUrl,
        currentCountryCode: data.props.pageProps.countryCode,
        languageCode: lang,
        theme: 'dark',
        google_target_id: shouldLoadOverseaAnalytics ? GOOGLE_TARGET_ID : '',
        apiConfig: {
          middlewareConfig: { errorMiddleware: createErrorMiddleware, logging: true },
          canvasMiddlewareConfig: { errorMiddleware: createCanvasErrorMiddleware, logging: true },

        },
        userInfo: {
          uid: (uid && token) ? uid : '',
          token: (uid && token) ? token : '',
          avatar: '',
          screenName: '',
        },
      });
      await initMTLogin({ isOversea: shouldLoadOverseaAnalytics });
    } catch (error) {
      console.log('error', error);
    }

    return {
      languageCode: lang,
    };
  },
});
